"use client";

import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { ChevronRight, Heart, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useJourneyData } from "@/hooks/use-journey-data";
import { useNeighborhoodMap } from "@/hooks/use-neighborhood-map";
import { publicFetch } from "@/lib/auth";
import type { NeighborhoodInsight } from "@/lib/contentful/neighborhood-insights";
import { cn } from "@/lib/utils";

import { NeighborhoodFacts } from "@/components/neighborhoods/neighborhood-facts";
import { NeighborhoodStats } from "@/components/neighborhoods/neighborhood-stats";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface NeighborhoodSelectorProps {
  informativeOnly?: boolean;
}

export function NeighborhoodSelector({
  informativeOnly = false,
}: NeighborhoodSelectorProps) {
  const journeyDataHook = informativeOnly ? null : useJourneyData();

  const [selectedNeighborhood, setSelectedNeighborhood] = useState<
    string | null
  >(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState<string | null>(null);

  const selectedNeighborhoods = informativeOnly
    ? new Set<string>()
    : new Set(journeyDataHook?.journeyData?.neighborhoods || []);

  const handleNeighborhoodSelect = useCallback((name: string) => {
    setSelectedNeighborhood(name);
    setIsDetailsOpen(true);
  }, []);

  useNeighborhoodMap({
    selectedNeighborhoods,
    onNeighborhoodSelect: handleNeighborhoodSelect,
  });

  const handleSaveNeighborhood = async (name: string) => {
    if (informativeOnly || !journeyDataHook) return;

    setIsSaving(name);
    const newNeighborhoods = selectedNeighborhoods.has(name)
      ? Array.from(selectedNeighborhoods).filter((n) => n !== name)
      : [...Array.from(selectedNeighborhoods), name];

    journeyDataHook.updateJourney.mutate(
      { neighborhoods: newNeighborhoods },
      {
        onSettled: () => setIsSaving(null),
      },
    );
  };

  const [neighborhoodInsights, setNeighborhoodInsights] =
    useState<Record<string, NeighborhoodInsight>>();

  useEffect(() => {
    const fetchNeighborhoodInsights = async () => {
      const data = await publicFetch("/api/neighborhood-insights");
      const insights = await data.json();

      setNeighborhoodInsights(insights);
    };

    fetchNeighborhoodInsights();
  }, []);

  return (
    <div className="relative w-full h-[600px] rounded-xl overflow-hidden">
      <div id="map" className="absolute h-full w-full" />

      {/* Only show Selected Neighborhoods Panel if not in informative mode */}
      {!informativeOnly && (
        <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg p-4 max-w-xs">
          <h3 className="font-semibold mb-2">Selected Neighborhoods</h3>
          {selectedNeighborhoods.size === 0 ? (
            <p className="text-sm text-gray-600">
              Click neighborhoods on the map to select areas you're interested
              in
            </p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {Array.from(selectedNeighborhoods).map((name) => (
                <div
                  key={name}
                  className="flex items-center justify-between bg-gray-50 rounded-md p-2"
                >
                  <span className="text-sm">{name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSaveNeighborhood(name)}
                    disabled={isSaving === name}
                    className="h-8 w-8 p-0"
                  >
                    {isSaving === name ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <Sheet open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[540px] pt-4">
          <SheetHeader className="pr-8 h-10 ">
            <SheetTitle className="flex items-center justify-between">
              <span>{selectedNeighborhood}</span>

              {/* Only show favorite button if not in informative mode */}
              {!informativeOnly && selectedNeighborhood && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "min-w-[120px]",
                    selectedNeighborhoods.has(selectedNeighborhood)
                      ? "bg-primary/10 text-primary"
                      : "bg-white",
                    "transition-all duration-200",
                  )}
                  onClick={() => handleSaveNeighborhood(selectedNeighborhood)}
                  disabled={isSaving === selectedNeighborhood}
                >
                  {isSaving === selectedNeighborhood ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </span>
                  ) : selectedNeighborhoods.has(selectedNeighborhood) ? (
                    <span className="flex items-center gap-2">
                      <Heart className="h-4 w-4 fill-current" />
                      Favorited
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Add to Favorites
                    </span>
                  )}
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>

          {neighborhoodInsights &&
          selectedNeighborhood &&
          neighborhoodInsights[selectedNeighborhood] ? (
            <ScrollArea className="pr-4 h-[calc(100%-1rem)]">
              <div className="space-y-6 py-4">
                <p className="text-gray-600">
                  {neighborhoodInsights[selectedNeighborhood].overview}
                </p>

                <NeighborhoodStats
                  medianPropertyPrice={
                    neighborhoodInsights[selectedNeighborhood]
                      .medianPropertyPrice
                  }
                  medianPropertyPricePerSqft={
                    neighborhoodInsights[selectedNeighborhood]
                      .medianPropertyPricePerSqft
                  }
                  medianSoldPropertyPrice={
                    neighborhoodInsights[selectedNeighborhood]
                      .medianSoldPropertyPrice
                  }
                />

                <div className="space-y-2">
                  <h4 className="font-semibold">Neighborhood Highlights</h4>
                  <ul className="space-y-2">
                    {neighborhoodInsights[selectedNeighborhood].highlights.map(
                      (highlight, i) => (
                        <li
                          key={highlight}
                          className="flex items-center text-sm"
                        >
                          <ChevronRight className="h-4 w-4 text-primary mr-2" />
                          {highlight}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Lifestyle</h4>
                  <p className="text-sm text-gray-600">
                    {neighborhoodInsights[selectedNeighborhood].lifestyle}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Walk Score</span>
                      <span className="text-sm font-semibold">
                        {neighborhoodInsights[selectedNeighborhood].walkScore}
                        /100
                      </span>
                    </div>
                    <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{
                          width: `${neighborhoodInsights[selectedNeighborhood].walkScore}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Transit Score</span>
                      <span className="text-sm font-semibold">
                        {
                          neighborhoodInsights[selectedNeighborhood]
                            .transitScore
                        }
                        /100
                      </span>
                    </div>
                    <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{
                          width: `${neighborhoodInsights[selectedNeighborhood].transitScore}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {neighborhoodInsights[selectedNeighborhood].funFacts && (
                  <NeighborhoodFacts
                    facts={neighborhoodInsights[selectedNeighborhood].funFacts}
                  />
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="h-full flex items-center justify-center">
              <p className="text-gray-500 text-center px-4">
                No detailed information available for this neighborhood yet.
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
